/* eslint-disable no-console */
import { useSession } from '@acp/feature/auth';
import { parseUNSAFEOriginalComplianceUserId } from '@acp/shared';
import { datadogRum } from '@datadog/browser-rum';
import { useEffect } from 'react';

export type DatadogEvent = {
    actionName: string;
    eventContext?: object;
};

export function setupDatadog() {
    if (import.meta.env.VITE_PUBLIC_DATADOG_RUM_ENABLE !== '1') return;

    if (import.meta.env.VITE_PUBLIC_DATADOG_RUM_APPLICATION_ID === undefined) {
        console.log('Check Datadog Rum Public Env Vars. VITE_PUBLIC_DATADOG_RUM_APPLICATION_ID is not defined.');
        return;
    }

    if (import.meta.env.VITE_PUBLIC_DATADOG_RUM_CLIENT_TOKEN === undefined) {
        console.log('Check Datadog Rum Public Env Vars. VITE_PUBLIC_DATADOG_RUM_CLIENT_TOKEN is not defined');
        return;
    }

    if (import.meta.env.VITE_PUBLIC_DATADOG_RUM_ENV === undefined) {
        console.log('Check Datadog Rum Public Env Vars. VITE_PUBLIC_DATADOG_RUM_ENV is not defined');
        return;
    }

    if (import.meta.env.VITE_PUBLIC_DATADOG_RUM_SERVICE === undefined) {
        console.log('Check Datadog Rum Public Env Vars. VITE_PUBLIC_DATADOG_RUM_SERVICE is not defined');
        return;
    }

    datadogRum.init({
        applicationId: import.meta.env.VITE_PUBLIC_DATADOG_RUM_APPLICATION_ID,
        clientToken: import.meta.env.VITE_PUBLIC_DATADOG_RUM_CLIENT_TOKEN,
        site: 'datadoghq.com',
        service: import.meta.env.VITE_PUBLIC_DATADOG_RUM_SERVICE as string,
        env: import.meta.env.VITE_PUBLIC_DATADOG_RUM_ENV,
        allowedTracingUrls: [import.meta.env.VITE_PUBLIC_BFF_URL as string],
        traceSampleRate: 10,
        sessionSampleRate: 100,
        sessionReplaySampleRate: 100,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'mask-user-input',
    });
}

export function addDatadogEvent({ actionName, eventContext }: DatadogEvent) {
    if (import.meta.env.VITE_PUBLIC_DATADOG_RUM_ENABLE !== '1') return;

    datadogRum.addAction(actionName, eventContext);
}

// This sets user-specific attributes for Datadog RUM
// https://docs.datadoghq.com/real_user_monitoring/browser/advanced_configuration/?tab=npm#user-session
export function useSetDatadogUserAttributes() {
    const session = useSession();

    useEffect(() => {
        if (import.meta.env.VITE_PUBLIC_DATADOG_RUM_ENABLE !== '1' || session === undefined) return;

        datadogRum.setUser({
            id: session.complianceUserId.toString(),
            name: session.complianceUserName,
            email: session.complianceUserEmail,
            compliance_agency_id: session.complianceAgencyId,
            compliance_agency_name: session.complianceAgencyName,
            compliance_agency_state: session.complianceAgencyState,
            is_impersonating: session.isImpersonating ? 'Yes' : 'No',
            unsafe_original_compliance_user_id: parseUNSAFEOriginalComplianceUserId(
                session.UNSAFE_originalComplianceUserId
            ),
        });
    }, [session]);
}
